// Third-party
import React from 'react';
import classNames from 'classnames';
import Dotdotdot from 'react-dotdotdot';
import isEqual from 'lodash/isEqual';

// Components
import { FormattedMessage } from '../../../../utils/i18n';
import { ProposalDonutChart } from '../../../../views/components/charts';
import { HoldingIcon } from '../../../../views/components/common';
import * as ProposalNS from '../../Proposal';
import { ParenthesesLabel } from '../../../../views/components/misc';
import { PROPOSAL_ITEM_PURPOSE } from '../../utils/constants';

import Nullable from '@/types/nullable';
import Dictionary from '@/types/dictionary';
import DonutData from '@/types/donut-data';
import DonutConfig from '@/types/donut-config';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import AccessibilityDescription from '../misc/AccessibilityDescription';
import DonutHeader from '@/types/donut-header';

type Props = {
  donutData: DonutData;
  className?: string;
  showHeader: boolean;
  showLegend: boolean;
  showTotal: boolean;
  showCurrency?: boolean;
  configOptions: Partial<ProposalNS.DonutChartConfigOptions>;
  colorsDict: Nullable<Dictionary<string>>;
  locale: Locale;
  accessibilityDescription?: LocalizedString;
};

export const getDonutConfig = (): DonutConfig => ({
  colorsDict: null,
  legend: {
    show: true,
    position: 'right',
  },
  accessibilityDescription: undefined,
  chartOptions: {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 200,
      width: 200,
    },
    plotOptions: {
      pie: {
        borderWidth: 0,
        shadow: false,
        center: ['50%', '50%'],
        states: {
          hover: {
            enabled: false,
          },
        },
        innerSize: '80%',
        size: '100%',
      },
      series: {
        enableMouseTracking: false,
        animation: false,
        states: {
          hover: {
            enabled: false,
          },
        },
        label: { enabled: false },
        dataLabels: {
          enabled: false,
        },
      },
    },
    title: {
      text: undefined,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
  },
});

class ProposalDonut extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  renderHeader = () => {
    const {
      showHeader,
      showCurrency,
      donutData: { donutHeader },
    } = this.props;

    if (!showHeader || !donutHeader) {
      return null;
    }

    const purposeFlag = Object.values(PROPOSAL_ITEM_PURPOSE).find(flag => donutHeader.flags?.includes(flag));

    return (
      <div className="donut-header" style={{ '--holding-accent-color': donutHeader.color } as React.CSSProperties}>
        <HoldingIcon type={donutHeader.type} color={donutHeader.color} className={purposeFlag} />

        <Dotdotdot useNativeClamp clamp={2}>
          <span data-private data-dd-privacy="mask">
            {donutHeader.name}
          </span>
        </Dotdotdot>

        {showCurrency && donutHeader.currency && <ParenthesesLabel text={donutHeader.currency} />}
      </div>
    );
  };

  getDataItems(itemsData?: DonutHeader) {
    return [itemsData?.name || ''];
  }

  render() {
    const {
      configOptions,
      donutData: { data, title, donutHeader },
      className,
      showLegend,
      showTotal,
      colorsDict,
      locale,
      accessibilityDescription,
    } = this.props;

    const hasData = data.some(itemData => itemData.some(datum => datum.y > 0));

    return (
      <div className={classNames('proposal-donut', className)}>
        {this.renderHeader()}

        {hasData && accessibilityDescription && (
          <AccessibilityDescription description={accessibilityDescription} items={this.getDataItems(donutHeader)} locale={locale} />
        )}

        {hasData && (
          <ProposalDonutChart
            data={data}
            configOptions={configOptions}
            showLegend={showLegend}
            showTotal={showTotal}
            colorsDict={colorsDict}
            locale={locale}
            title={title}
          />
        )}

        {!hasData && (
          <div className="donut-empty-state">
            <FormattedMessage id="Proposal.DataUnavailable" locale={locale} />
          </div>
        )}
      </div>
    );
  }
}

export type ProposalDonutProps = Props;

export default ProposalDonut;
